<template>
    <div class="sch-ctrl-page">
        <HeaderBox class="sc-page-head" :title="`${$t('건물 운영 관리')} > ${$t('제어 스케줄 관리')}`" />

        <div class="sc-split">
            <ScheduleListCard
                ref="list"
                :rows="schdList"
                :selected-idx="schdCtrlIdx"
                @select="handleSelect"
                @add="startEdit(null)"
                @delete="deleteChecked"
                @status="changeStatusChecked"
                @toggle="handleToggle"
                @except-batch="openExceptBatchModal"
            />
            <ScheduleDetailView
                ref="detailView"
                :schd-ctrl-idx="schdCtrlIdx"
                :list-row="selectedRow"
                @edit="(detail) => startEdit(detail.schdCtrlIdx)"
                @close="schdCtrlIdx = null"
            />
        </div>

        <!-- 추가/편집: 목록 위로 열리는 오른쪽 드로어 (목록 맥락 유지) -->
        <transition name="sc-drawer">
            <div v-if="editing" class="sc-drawer">
                <div class="sc-drawer-backdrop" @click="requestCloseEdit"></div>
                <aside class="sc-drawer-panel" role="dialog" aria-modal="true" :aria-label="$t('제어 스케줄 편집')">
                    <ScheduleEditWorkspace
                        ref="workspace"
                        :key="editKey"
                        :schd-ctrl-idx="editIdx"
                        @saved="handleSaved"
                        @cancel="editing = false"
                    />
                </aside>
            </div>
        </transition>

        <b-modal
            modal-class="sch-modal"
            ref="ExceptionDayBatchModal"
            size="lg"
            centered
            @hidden="closeExceptBatchModal"
        >
            <template #modal-header>
                <h5>{{ $t("예외일 설정") }}</h5>
            </template>
            <template #modal-footer>
                <ModalFooter
                    :disabled="isExceptBatchSaving"
                    @confirm="applyExceptBatch"
                    @cancel="closeExceptBatchModal"
                />
            </template>
            <ExceptionDayBatchPanel ref="ExceptionDayBatchPanel" :targetSchedules="exceptBatchTargets" />
        </b-modal>
    </div>
</template>

<script>
import backEndApi from "@src/api/backEndApi";
import HeaderBox from "@views/component/headerBox/analysis/Header.vue";
import ScheduleListCard from "./components/ScheduleListCard.vue";
import ScheduleDetailView from "./components/ScheduleDetailView.vue";
import ScheduleEditWorkspace from "./components/ScheduleEditWorkspace.vue";
import ModalFooter from "./components/ModalFooter.vue";
import ExceptionDayBatchPanel from "./panels/ExceptionDayBatchPanel.vue";
import { fetchScheduleList, resetCaches, ApiError } from "./utils/scheduleApi";
import * as popupMessages from "@src/consts/popupMessageConsts";

export default {
    components: {
        HeaderBox,
        ScheduleListCard,
        ScheduleDetailView,
        ScheduleEditWorkspace,
        ModalFooter,
        ExceptionDayBatchPanel,
    },
    data() {
        return {
            editing: false, // 편집 드로어 열림 여부
            schdList: [],
            schdCtrlIdx: null, // 상세 패널에 표시 중인 스케줄
            editIdx: null, // 편집 워크스페이스 대상 (null = 신규)
            editKey: 0, // 워크스페이스를 새로 만들기 위한 키
            exceptBatchTargets: [],
            isExceptBatchSaving: false,
        };
    },
    computed: {
        selectedRow() {
            return this.schdList.find((row) => row.schdCtrlIdx === this.schdCtrlIdx) || null;
        },
    },
    async created() {
        resetCaches();
        await this.getSchdCtrlList();
    },
    beforeDestroy() {
        window.removeEventListener("keydown", this.onKeydown);
    },
    watch: {
        editing(open) {
            if (open) window.addEventListener("keydown", this.onKeydown);
            else window.removeEventListener("keydown", this.onKeydown);
        },
    },
    async beforeRouteLeave(to, from, next) {
        if (this.editing && this.$refs.workspace) {
            next(await this.$refs.workspace.canLeave());
        } else {
            next();
        }
    },
    methods: {
        async getSchdCtrlList() {
            try {
                this.schdList = await fetchScheduleList();
            } catch (e) {
                this.schdList = [];
                if (e instanceof ApiError) this.alertWarning(e.message, "", e.detail);
                else this.alertWarning("data fetch error");
                console.error(e);
            }
        },

        /** 목록/상세를 서버 기준으로 다시 맞춘다 */
        async refresh() {
            await this.getSchdCtrlList();
            if (this.schdCtrlIdx && !this.selectedRow) this.schdCtrlIdx = null;
            if (this.schdCtrlIdx && this.$refs.detailView) await this.$refs.detailView.reload();
        },

        handleSelect(row) {
            this.schdCtrlIdx = row.schdCtrlIdx;
        },

        /** 드로어 닫기 요청 (백드롭 클릭/ESC): 미저장 변경이 있으면 확인 후 닫는다 */
        requestCloseEdit() {
            if (this.$refs.workspace) this.$refs.workspace.requestCancel();
            else this.editing = false;
        },

        onKeydown(e) {
            if (e.key !== "Escape") return;
            // 내부 모달(이벤트/예외일/그룹·설비 선택)이 열려 있으면 그 모달이 먼저 닫히도록 둔다
            if (document.querySelector(".modal.show")) return;
            this.requestCloseEdit();
        },

        startEdit(idx) {
            this.editIdx = idx;
            this.editKey += 1;
            this.editing = true;
        },

        async handleSaved(savedIdx) {
            resetCaches();
            await this.getSchdCtrlList();
            this.editing = false;
            if (savedIdx !== null && savedIdx !== undefined) this.schdCtrlIdx = savedIdx;
            if (this.schdCtrlIdx && !this.selectedRow) this.schdCtrlIdx = null;
            // 같은 스케줄을 편집한 경우 idx 가 바뀌지 않으므로 직접 다시 읽는다
            this.$nextTick(() => this.$refs.detailView && this.$refs.detailView.reload());
        },

        /* ---------------- 예외일 일괄 설정 ---------------- */
        async openExceptBatchModal(checked) {
            if (this.isEmpty(checked)) {
                this.alertNoti(popupMessages.SCHEDULE_CONTROL_EXCEPT_BATCH_NO_SCHEDULE_MESSAGE);
                return;
            }
            this.exceptBatchTargets = checked.map((item) => ({
                schdCtrlIdx: item.schdCtrlIdx,
                schdCtrlName: item.schdCtrlName,
            }));
            this.$refs.ExceptionDayBatchModal.show();
            this.$nextTick(() => {
                if (this.$refs.ExceptionDayBatchPanel) this.$refs.ExceptionDayBatchPanel.reset();
            });
        },

        closeExceptBatchModal() {
            this.$refs.ExceptionDayBatchModal.hide();
            this.exceptBatchTargets = [];
            this.isExceptBatchSaving = false;
        },

        async applyExceptBatch() {
            if (!this.$refs.ExceptionDayBatchPanel) return;

            this.isExceptBatchSaving = true;
            try {
                const result = await this.$refs.ExceptionDayBatchPanel.apply();
                if (result && result.success) {
                    await this.refresh();
                    this.closeExceptBatchModal();
                }
            } finally {
                this.isExceptBatchSaving = false;
            }
        },

        /* ---------------- 삭제 / 사용 여부 ---------------- */
        /** allSettled 결과 중 실패(거절 또는 200 이 아닌 응답) 개수 */
        countFailed(results) {
            return results.filter((r) => r.status === "rejected" || (r.value && r.value.status != 200)).length;
        },

        async deleteChecked(checked) {
            if (this.isEmpty(checked)) {
                this.alertNoti(popupMessages.COMMON_DELETE_NOTI_POPUP_MESSAGE);
                return;
            }
            try {
                const confirm = await this.alertConfirmWarning("선택한 목록을 삭제하시겠습니까?");
                if (!confirm.value) return;

                const results = await Promise.allSettled(
                    checked.map((item) => backEndApi.equipControl.deleteEquipCtrlSch(item.schdCtrlIdx)),
                );
                const failed = this.countFailed(results);
                await this.alertNoti(
                    failed
                        ? popupMessages.COMMON_REMOVE_ERROR_POPUP_MESSAGE
                        : popupMessages.COMMON_DELETE_POPUP_MESSAGE,
                );

                this.$refs.list.clearChecked();
                await this.getSchdCtrlList();
                if (this.schdCtrlIdx && !this.selectedRow) this.schdCtrlIdx = null;
            } catch (e) {
                this.alertNoti(popupMessages.COMMON_REMOVE_ERROR_POPUP_MESSAGE);
                console.error(e);
            }
        },

        async changeStatusChecked(checked, enabled) {
            if (this.isEmpty(checked)) {
                this.alertNoti(popupMessages.COMMON_STATUS_CHANGE_NOTI_POPUP_MESSAGE);
                return;
            }
            try {
                const results = await Promise.allSettled(
                    checked.map((item) =>
                        backEndApi.equipControl.setSchdCtrlEnabled({ schdCtrlIdx: item.schdCtrlIdx, enabled }),
                    ),
                );
                const failed = this.countFailed(results);
                await this.alertNoti(
                    failed
                        ? popupMessages.COMMON_STATUS_CHANGE_ERROR_POPUP_MESSAGE
                        : popupMessages.COMMON_STATUS_CHANGE_SUCCESS_POPUP_MESSAGE,
                );
                await this.refresh();
            } catch (e) {
                console.error(e);
                this.alertNoti(popupMessages.COMMON_STATUS_CHANGE_ERROR_POPUP_MESSAGE);
            }
        },

        /** 행 스위치: 성공 시 조용히 반영하고 실패했을 때만 알린다 */
        async handleToggle(row, enabled) {
            try {
                const result = await backEndApi.equipControl.setSchdCtrlEnabled({
                    schdCtrlIdx: row.schdCtrlIdx,
                    enabled,
                });
                if (result.status != 200) {
                    this.alertWarning(
                        `${result.data.message}`,
                        "",
                        `${typeof result.data.detail == "string" ? result.data.detail : ""}`,
                    );
                }
            } catch (e) {
                console.error(e);
                this.alertNoti(popupMessages.COMMON_STATUS_CHANGE_ERROR_POPUP_MESSAGE);
            }
            await this.refresh();
        },
    },
};
</script>

<style lang="scss" src="./styles/schedule-control.scss"></style>
